// İSG uzmanı portalı — kullanıcı oluştur/düzenle formu (Modal + RHF + Zod).
//
// api-contract §12.1:
//  - create `POST /users` `{ email, firstName, lastName, password, roleUuids? }`
//  - update `PUT /users/{uuid}` `{ firstName, lastName, isActive, roleUuids? }` (rol seti replace)
// Rol seçici kaynağı yalnız `GET /roles` (§12.2 — atanabilir roller). Parola politikası
// (§2: min 8 + harf + rakam) FE'de de doğrulanır; backend `400 PASSWORD_POLICY_ERROR`.
// E-posta çakışması → `409 USER_ALREADY_EXISTS`. **Parola yalnız istekte taşınır**:
// type=password + autocomplete=new-password; gönderim sonrası formla birlikte temizlenir
// (log/URL'e girmez). **Kendini pasifleştirme** UI'da engellenir (mevcut kullanıcıysa
// aktif/pasif toggle disabled → `409 CANNOT_DEACTIVATE_SELF` önlenir).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  Spinner,
  type UserDto,
  resolveApiErrorMessage,
  usersApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useMe } from '../../lib/useMe';
import { usersKeys } from '../../lib/usersKeys';

interface UserFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verilirse düzenleme (liste satırı); yoksa oluşturma. */
  user?: UserDto | null;
  onSuccess?: () => void;
}

export function UserFormModal({ open, onOpenChange, user, onSuccess }: UserFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { me } = useMe();
  const isEdit = Boolean(user);
  const [showPassword, setShowPassword] = useState(false);

  // Kendini pasifleştirme engeli — mevcut kullanıcı kendi kaydını düzenliyorsa
  // aktif/pasif toggle kilitlenir (409 CANNOT_DEACTIVATE_SELF önlenir; §12).
  const isSelf = isEdit && user != null && me != null && user.uuid === me.uuid;

  // Atanabilir roller (yalnız GET /roles; §12.2) — form açıkken çekilir.
  const rolesQuery = useQuery({
    queryKey: usersKeys.roles(),
    queryFn: () => usersApi.listAssignableRoles(),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  // Türkçe harf sırası — rol adları katalog verisidir, arayüz diliyle ilgisiz (bkz.
  // `lib/format.ts`teki `initials()`in aynı gerekçesi).
  const roles = useMemo(
    () => [...(rolesQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'tr')),
    [rolesQuery.data],
  );

  // Düzenlemede tam rol setini (`roleUuids`) form ön-seçimi için detaydan çekeriz
  // (liste satırı yalnız tek `roleName` taşır — §12 DTO ayrımı).
  const detailQuery = useQuery({
    queryKey: user ? usersKeys.detail(user.uuid) : usersKeys.detail('none'),
    queryFn: () => usersApi.getUser((user as UserDto).uuid),
    enabled: open && isEdit && user != null,
    staleTime: 0,
  });

  const schema = useMemo(
    () =>
      z
        .object({
          firstName: z.string().trim().min(1, t('users.validation.firstNameRequired')),
          lastName: z.string().trim().min(1, t('users.validation.lastNameRequired')),
          email: z.string().trim().optional(),
          password: z.string().optional(),
          isActive: z.boolean(),
          roleUuids: z.array(z.string()).min(1, t('users.validation.rolesRequired')),
        })
        .superRefine((val, ctx) => {
          // E-posta + parola yalnız oluşturmada — düzenlemede uç bu alanları taşımaz.
          if (isEdit) {
            return;
          }
          if (!val.email) {
            ctx.addIssue({
              path: ['email'],
              code: z.ZodIssueCode.custom,
              message: t('users.validation.emailRequired'),
            });
          } else if (!z.string().email().safeParse(val.email).success) {
            ctx.addIssue({
              path: ['email'],
              code: z.ZodIssueCode.custom,
              message: t('users.validation.emailInvalid'),
            });
          }
          const pw = val.password ?? '';
          if (pw.length < 8 || !/[A-Za-z]/.test(pw) || !/\d/.test(pw)) {
            ctx.addIssue({
              path: ['password'],
              code: z.ZodIssueCode.custom,
              message: t('users.validation.passwordPolicy'),
            });
          }
        }),
    [t, isEdit],
  );
  type FormValues = z.infer<typeof schema>;

  const detail = detailQuery.data;
  const { register, handleSubmit, reset, control, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      firstName: detail?.firstName ?? user?.firstName ?? '',
      lastName: detail?.lastName ?? user?.lastName ?? '',
      email: '',
      password: '',
      isActive: detail?.isActive ?? user?.isActive ?? true,
      roleUuids: detail?.roleUuids ?? [],
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      if (user) {
        return usersApi.updateUser(user.uuid, {
          firstName: values.firstName,
          lastName: values.lastName,
          // Kendini pasifleştirme guard'ı — self ise her koşulda aktif kalır.
          isActive: isSelf ? true : values.isActive,
          roleUuids: values.roleUuids,
        });
      }
      return usersApi.createUser({
        email: values.email ?? '',
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password ?? '',
        roleUuids: values.roleUuids,
      });
    },
    onSuccess: () => {
      // Önek invalidasyonu — tüm liste (host + yönetim) varyantlarını + erişim sondasını kapsar.
      queryClient.invalidateQueries({ queryKey: usersKeys.list() });
      queryClient.invalidateQueries({ queryKey: usersKeys.access() });
      if (user) {
        queryClient.invalidateQueries({ queryKey: usersKeys.detail(user.uuid) });
      }
      reset(); // parola dahil tüm alanlar temizlenir (değer bellek/URL'de kalmaz).
      setShowPassword(false);
      onSuccess?.();
      onOpenChange(false);
    },
  });

  const detailLoading = isEdit && detailQuery.isPending;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          setShowPassword(false);
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('users.edit.title') : t('users.create.title')}
      description={isEdit ? t('users.edit.description') : t('users.create.description')}
      closeLabel={t('users.common.close')}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('users.common.cancel')}
          </Button>
          <Button
            type="submit"
            form="user-form"
            loading={mutation.isPending}
            disabled={detailLoading}
          >
            {mutation.isPending ? (
              <Spinner label={t('users.common.save')} />
            ) : isEdit ? (
              t('users.common.save')
            ) : (
              t('users.common.create')
            )}
          </Button>
        </>
      }
    >
      <form
        id="user-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        {detailLoading ? (
          <div className="flex items-center justify-center py-6">
            <Spinner label={t('users.edit.loading')} />
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                htmlFor="user-first"
                label={t('users.field.firstName')}
                error={formState.errors.firstName?.message}
                required
              >
                <Input
                  id="user-first"
                  autoComplete="off"
                  invalid={Boolean(formState.errors.firstName)}
                  {...register('firstName')}
                />
              </Field>
              <Field
                htmlFor="user-last"
                label={t('users.field.lastName')}
                error={formState.errors.lastName?.message}
                required
              >
                <Input
                  id="user-last"
                  autoComplete="off"
                  invalid={Boolean(formState.errors.lastName)}
                  {...register('lastName')}
                />
              </Field>
            </div>

            {isEdit ? (
              // E-posta düzenlemede değişmez (uç taşımaz) — salt görüntü.
              <Field htmlFor="user-email-ro" label={t('users.field.email')}>
                <Input id="user-email-ro" value={user?.email ?? ''} disabled readOnly />
              </Field>
            ) : (
              <>
                <Field
                  htmlFor="user-email"
                  label={t('users.field.email')}
                  error={formState.errors.email?.message}
                  required
                >
                  <Input
                    id="user-email"
                    type="email"
                    autoComplete="off"
                    invalid={Boolean(formState.errors.email)}
                    {...register('email')}
                  />
                </Field>
                <Field
                  htmlFor="user-password"
                  label={t('users.field.password')}
                  hint={t('users.validation.passwordPolicy')}
                  error={formState.errors.password?.message}
                  required
                >
                  <div className="relative">
                    <Input
                      id="user-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      className="pr-10"
                      invalid={Boolean(formState.errors.password)}
                      {...register('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted hover:bg-surface-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={
                        showPassword ? t('users.field.passwordHide') : t('users.field.passwordShow')
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Eye className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </Field>
              </>
            )}

            {isEdit ? (
              <Field htmlFor="user-active" label={t('users.field.status')}>
                <label className="flex items-center gap-2 text-sm font-medium text-fg">
                  <input
                    id="user-active"
                    type="checkbox"
                    className="h-4 w-4 accent-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={isSelf}
                    {...register('isActive')}
                  />
                  {t('users.field.isActive')}
                </label>
                {isSelf ? (
                  <p className="text-xs text-muted">{t('users.field.selfActiveHint')}</p>
                ) : null}
              </Field>
            ) : null}

            {/* Rol seçici — checkbox grubu; erişilebilirlik için native fieldset/legend
                (Field/input eşleşmesi tekil giriş içindir, çoklu seçim değil). */}
            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-sm font-medium text-fg">{t('users.field.roles')}</legend>
              {rolesQuery.isPending ? (
                <Spinner label={t('users.roles.loading')} />
              ) : rolesQuery.isError ? (
                <Alert tone="danger">{resolveApiErrorMessage(rolesQuery.error, t)}</Alert>
              ) : roles.length === 0 ? (
                <p className="text-sm text-muted">{t('users.roles.empty')}</p>
              ) : (
                <Controller
                  control={control}
                  name="roleUuids"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-muted/40 p-3">
                      {roles.map((role) => {
                        const checked = field.value.includes(role.uuid);
                        return (
                          <label
                            key={role.uuid}
                            className="flex items-center gap-2 text-sm text-fg"
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-[var(--color-primary)]"
                              checked={checked}
                              onChange={(e) => {
                                field.onChange(
                                  e.target.checked
                                    ? [...field.value, role.uuid]
                                    : field.value.filter((u) => u !== role.uuid),
                                );
                              }}
                            />
                            {role.name}
                          </label>
                        );
                      })}
                    </div>
                  )}
                />
              )}
              {formState.errors.roleUuids ? (
                <p className="text-xs font-medium text-danger" role="alert">
                  {formState.errors.roleUuids.message}
                </p>
              ) : null}
            </fieldset>
          </>
        )}
      </form>
    </Modal>
  );
}
